import { State } from 'src/replicant/State';
import {
  TournamentType,
  WeeklySchedule,
  flyingHighUnlockLevel,
  match2TournamentRuleset,
  match2TournamentUnlockLevel,
  match2TournamentRuleset as ruleset,
} from './match2-tournament.ruleset';
import { duration } from 'src/replicant/utils/duration';
import { Match2Reward } from '../rewards/match2-rewards.ruleset';
import features from 'src/replicant/ruleset/features';

/**
 * Return true if the feature is enabled
 */
export function isTournamentFeatureEnabled(state: State): boolean {
  // Return false if the feature is not enabled
  return features(state).match2StarTournament;
}

/**
 * Return true if the feature is unlocked
 */
export function isTournamentFeatureUnlocked(state: State): boolean {
  // Return false if the feature is not enabled
  return (
    isTournamentFeatureEnabled(state) &&
    state.match2.level >= match2TournamentUnlockLevel
  );
}

/**
 * Return true if the feature is enabled, unlocked (from level 25) and if there are ongoing or unclaimed tournaments
 */
export function canPlay(state: State, now: number): boolean {
  // Return false if the feature is not enabled
  if (!isTournamentFeatureEnabled(state)) return false;

  // Return false if match2 level is not set yet
  if (!state.match2?.level) return false;

  // Return false if match2 level is not high enough to play tournament
  if (state.match2.level < ruleset.unlockLevel) return false;

  // Return true if there is any unclaimed tournament event
  const unclaimed = getUnclaimedEvents(state);
  if (unclaimed.length > 0) return true;

  // Check if there is any tournament event in progress
  const period = getTournamentPeriod(state, now);
  return !!period;
}

/**
 * Return ongoing tournament event or null if there is none or not started yet
 */
export function getOngoingEvent(state: State, now: number) {
  const period = getTournamentPeriod(state, now);
  if (!period || period.end < now) return null;

  const event = state.match2.tournament?.events[period.eventId];
  if (!event || event.finalRank) return null;
  return {
    id: period.eventId,
    end: period.end,
    ...state.match2.tournament.events[period.eventId],
  };
}

/**
 * Return a list of past tournaments with unclaimed rewards, sorted by earliest end date
 * This function is slightly different from the others as it will return only unclaimed events that have ended
 * according to the schedule, but not finished yet
 */
export function getPastUnclaimedEvents(state: State, now: number) {
  if (!state.match2.tournament?.events) return [];
  const events = state.match2.tournament.events;
  const eventKeys = Object.keys(state.match2.tournament.events);

  return eventKeys
    .filter((eventId) => {
      const period = getTournamentPeriodById(state, eventId);
      return (
        period &&
        period.end < now &&
        !events[eventId].claimed &&
        !events[eventId].finalRank &&
        events[eventId].opponentList.length > 0 // Only events with opponents can be claimed
      );
    })
    .map((eventId) => {
      const period = getTournamentPeriodById(state, eventId);
      return {
        id: eventId,
        end: period?.end || new Date(eventId).getTime(), // in case an old event has been deleted
        ...events[eventId],
      };
    })
    .sort((a, b) => a.end - b.end);
}

/**
 * Return a list of unfinished tournaments with unclaimed rewards, sorted by earliest end date
 */
export function getUnfinishedEvents(state: State) {
  if (!state.match2.tournament?.events) return [];
  const events = state.match2.tournament.events;
  const eventKeys = Object.keys(state.match2.tournament.events);

  return eventKeys
    .filter((event) => {
      return !events[event].claimed; // rank might not have been assigned yet - it will be assigned on the next createOrUpdateMatch2TournamentEvent
    })
    .map((event) => {
      const period = getTournamentPeriodById(state, event);
      return {
        id: event,
        end: period?.end || new Date(event).getTime(), // in case an old event has been deleted
        ...events[event],
      };
    })
    .sort((a, b) => a.end - b.end);
}

/**
 * Return a list of finished (finalRank assigned) tournaments with unclaimed rewards, sorted by earliest end date
 */
export function getUnclaimedEvents(state: State) {
  if (!state.match2.tournament?.events) return [];
  const events = state.match2.tournament.events;
  const eventKeys = Object.keys(state.match2.tournament.events);

  return eventKeys
    .filter((event) => {
      return (
        !events[event].claimed &&
        events[event].finalRank &&
        !!ruleset.getRewardsByRank(events[event].finalRank ?? 0) &&
        events[event].opponentList.length > 0 // Only events with opponents can be claimed
      );
    })
    .map((event) => {
      const period = getTournamentPeriodById(state, event);
      return {
        id: event,
        end: period?.end || new Date(event).getTime(), // in case an old event has been deleted
        ...events[event],
      };
    })
    .sort((a, b) => a.end - b.end);
}

/**
 * Return remaining time of current tournament schedule, in milliseconds
 */
export function getRemainingTime(state: State, now: number): number {
  const period = getTournamentPeriod(state, now);
  if (!period) return 0;
  return period.end - now;
}

/**
 * Get the most relevant event for the user, either unclaimed or ongoing
 */
export function getCurrentEvent(state: State, now: number) {
  // Prioritize unclaimed events
  const unclaimed = getUnclaimedEvents(state)[0];
  if (unclaimed) return unclaimed;

  // Then check for ongoing events
  const ongoing = getOngoingEvent(state, now);
  if (ongoing) return ongoing;

  // Otherwise return null
  return null;
}

/**
 * Get a list of pending rewards from finished tournaments
 */
export function getUnclaimedRewards(
  state: State,
  _now: number,
): Match2Reward[] {
  const unclaimedEvents = getUnclaimedEvents(state);
  if (!unclaimedEvents.length) return [];

  const rewards: Match2Reward[] = [];
  for (const event of unclaimedEvents) {
    const reward = ruleset.getRewardsByRank(event.finalRank!);
    if (reward) rewards.push(reward);
  }

  return rewards;
}

export function getRewardsByRank(rank: number): Match2Reward[] {
  const rewards: Match2Reward[] = [];

  // Append the reward for the rank, if any
  const reward = ruleset.getRewardsByRank(rank);
  if (reward) {
    rewards.push(reward);
  }

  return rewards;
}

export function getTournamentPeriodById(
  state: State,
  id: string,
): {
  eventId: string;
  start: number;
  end: number;
  type: TournamentType;
} | null {
  return getTournamentPeriod(state, new Date(id).getTime());
}

function canPlayFlyingHighTournament(state: State, now: number) {
  // Return false if match2 level is not set yet
  if (!state.match2?.level) return false;

  // Return false if match2 level is not high enough to play tournament
  if (state.match2.level < ruleset.flyingHighUnlockLevel) return false;

  const currentTournament = getTournamentPeriodForSchedule(
    ruleset.weeklyScheduleV2,
    now,
  );

  // Nothing blocking
  if (!currentTournament) {
    return true;
  }

  const startedTournamentType = getStoredTournamentType(
    state,
    currentTournament.eventId,
  );

  // If the current tournament was started as a star tournament
  // then that tournament needs to finish as a star tournament
  return startedTournamentType !== 'star';
}

export function isFlyingHighTournamentEnabled(
  state: State,
  now: number,
): boolean {
  return (
    features(state).match2FlyingHighTournament &&
    features(state).match2StarTournament &&
    state.match2.level >= flyingHighUnlockLevel &&
    canPlayFlyingHighTournament(state, now) &&
    // start on a tournament rollover
    (!!process.env.IS_DEVELOPMENT || now > 1697551140000)
  );
}

export function getTournamentPeriod(
  state: State,
  now: number,
): {
  eventId: string;
  start: number;
  end: number;
  type: TournamentType;
} | null {
  const schedule = isFlyingHighTournamentEnabled(state, now)
    ? ruleset.weeklyScheduleV2
    : ruleset.weeklySchedule;

  return getTournamentPeriodForSchedule(schedule, now);
}

export function getTournamentPeriodForSchedule(
  schedule: WeeklySchedule,
  now: number,
): {
  eventId: string;
  start: number;
  end: number;
  type: TournamentType;
} | null {
  const first = new Date(schedule[0].from).getTime();
  const day = duration({ days: 1 });
  const dayDiff = Math.floor((now - first) / day);
  const weekNum = Math.floor(dayDiff / 7);

  // Return null if the date is before the first tournament
  if (weekNum < 0) return null;

  // Create a week increment to make it recurring every week
  const incr = weekNum * (day * 7);

  const period = schedule.find(
    (item) =>
      now >= new Date(item.from).getTime() + incr &&
      now <= new Date(item.to).getTime() + incr,
  );

  // No schedule found
  if (!period) return null;

  const startTimestamp = new Date(period.from).getTime() + incr;

  // Return the period start and end time in milliseconds
  return {
    eventId: new Date(startTimestamp).toUTCString(), // our main event identifiers are strings
    start: startTimestamp,
    end: new Date(period.to).getTime() + incr,
    type: period.type,
  };
}

export function getStoredTournamentType(
  state: State,
  id: string,
): TournamentType | null {
  if (!state.match2.tournament?.events[id]) return null;
  return (state.match2.tournament?.events[id].type as TournamentType) || 'star';
}

export function getTournamenCurrentMultiplierIndex(
  state: State,
  now: number,
): number {
  if (!match2TournamentRuleset.tournamentMultiplier.length) return 0;
  const event = getOngoingEvent(state, now);
  return Math.min(
    event?.multiplier ?? 0,
    match2TournamentRuleset.tournamentMultiplier.length - 1,
  );
}
/**
 * Retrieves the tournament type based on the current event or tournament period.
 *
 * @param state - The current state of the application.
 * @param now - The current timestamp used to determine the active event or period.
 * @returns The tournament type if an event or period is found, otherwise `undefined`.
 */
export function getTournamentType(
  state: State,
  now: number,
): TournamentType | undefined {
  const event = getCurrentEvent(state, now);

  if (event) {
    return event.type as TournamentType;
  }

  const period = getTournamentPeriod(state, now);
  if (period) {
    return period.type;
  }

  return undefined;
}
